import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  Output,
} from '@angular/core';

import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { getSafeImageUrl } from '@app/core';

import { User } from '@app/shared';

@Component({
  selector: 'app-activities-panel',

  standalone: false,

  templateUrl: './activities-panel.component.html',

  styleUrls: ['./activities-panel.component.less'],

  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ActivitiesPanelComponent implements OnInit, OnChanges {
  @Input() activeActivityTabIndex = 0;

  @Input() loadingActivities = false;

  @Input() activitiesGroupedByDate: any[] = [];

  @Input() opportunity: any;

  @Input() chatName = '';

  @Input() showChat = true;

  @Input() users: User[] = [];

  @Input() loggedUser: User | undefined;

  @Input() getActivityIconWrapperClassFn: (activity: any) => string = () => '';

  getSafeImageUrl = getSafeImageUrl;

  @Input() getActivityIconFn: (activity: any) => string = () => '';

  @Input() getActivityIconColorFn: (activity: any) => string = () => '';

  @Input() getActivityTimeFn: (activity: any) => string = () => '';

  @Input() getActivityDueDateFn: (activity: any) => string = () => '';

  @Input() getUserInitialsFn: (activity: any) => string = () => '';

  @Input() getUserFullNameFn: (activity: any) => string = () => '';

  @Input() getActivityStatusClassFn: (state: any) => string = () => '';

  @Input() getActivityStatusLabelFn: (state: any) => string = () => '';

  @Output() openCreateActivity = new EventEmitter<void>();

  @Output() openRegisterNote = new EventEmitter<void>();

  @Output() openSendEmail = new EventEmitter<void>();

  @Output() markDone = new EventEmitter<any>();

  @Output() editActivity = new EventEmitter<any>();

  @Output() cancelActivity = new EventEmitter<any>();

  pendingCollapsed = false;

  historyCollapsed = false;

  noteCollapsed = false;

  messageAvatar(msg: any): string | null {
    const src = msg?.partner?.avatar || msg?.user?.partner?.avatar;

    const safe = src ? getSafeImageUrl(src) : '';

    return safe || null;
  }

  messageInitials(msg: any): string {
    const pick = (msg?.partner ||
      msg?.user?.partner || {
      name: msg?.user?.email ?? '',

      surName: '',
    }) as { name?: string; surName?: string };

    const parts = [pick.name, pick.surName]

      .filter(Boolean)

      .map((v) => v!.trim())

      .filter(Boolean);

    if (parts.length === 0 && msg?.user?.email) {
      const emailPart = msg.user.email.split('@')[0] || '';

      return emailPart.slice(0, 2).toUpperCase();
    }

    const initials = parts

      .slice(0, 2)

      .map((p) => p.charAt(0))

      .join('')

      .toUpperCase();

    return initials || '??';
  }

  displayAuthor(msg: any): string {
    if (!msg) return 'Usuario';

    if (
      msg.user?.id &&
      this.loggedUser?.id &&
      msg.user.id === this.loggedUser.id
    ) {
      return 'Tú';
    }

    // Prioridad: partner directo del mensaje

    const partnerName = [msg.partner?.name, msg.partner?.surName]

      .filter(Boolean)

      .join(' ')

      .trim();

    if (partnerName) return partnerName;

    // Fallback: buscar en el catálogo de usuarios por userId o partnerId

    const partnerId =
      msg.partner?.id ??
      msg.user?.partnerId ??
      msg.user?.partner?.id ??
      msg.user?.partner?.id;

    if (this.users?.length) {
      const found =
        this.users.find((u) => u.id === msg.user?.id) ||
        (partnerId
          ? this.users.find((u) => u.partner?.id === partnerId)
          : undefined);

      if (found?.partner) {
        const foundName = [found.partner.name, found.partner.surName]

          .filter(Boolean)

          .join(' ')

          .trim();

        if (foundName) return foundName;
      }
    }

    if (msg.user?.email) return msg.user.email;

    return 'Usuario';
  }

  get messagesGroupedByDate(): { label: string; messages: any[] }[] {
    const messages = this.opportunity?.chat?.chatMessages ?? [];

    const map = new Map<
      string,
      { label: string; messages: any[]; dateKey: number }
    >();

    messages.forEach((msg: any) => {
      const date = msg?.createdOn ? new Date(msg.createdOn) : null;

      const key = date ? date.toISOString().slice(0, 10) : 'sin-fecha';

      const dateKey = date ? date.getTime() : Number.MIN_SAFE_INTEGER;

      if (!map.has(key)) {
        map.set(key, { label: this.getDateLabel(date), messages: [], dateKey });
      }

      map.get(key)!.messages.push(msg);
    });

    // Ordenar mensajes dentro de cada grupo (más recientes primero)

    map.forEach((group) => {
      group.messages.sort((a, b) => {
        const da = a?.createdOn ? new Date(a.createdOn).getTime() : 0;

        const db = b?.createdOn ? new Date(b.createdOn).getTime() : 0;

        return db - da;
      });
    });

    // Ordenar grupos: Hoy primero, luego fechas descendentes, y al final "sin fecha"

    return Array.from(map.values())

      .sort((a, b) => b.dateKey - a.dateKey)

      .map(({ dateKey, ...rest }) => rest);
  }

  // Por ahora no mostramos mensajes enviados distintos al chat,

  // así que esta sección permanecerá oculta hasta implementar esa funcionalidad.

  get hasSentMessages(): boolean {
    return false;
  }

  get sentMessagesCount(): number {
    return 0;
  }

  ngOnInit(): void { }

  onCreateActivity(): void {
    this.openCreateActivity.emit();
  }

  onRegisterNote(): void {
    this.openRegisterNote.emit();
  }

  onSendEmail(): void {
    this.openSendEmail.emit();
  }

  onMarkDone(activity: any): void {
    this.markDone.emit(activity);
  }

  onEditActivity(activity: any): void {
    this.editActivity.emit(activity);
  }

  onCancelActivity(activity: any): void {
    this.cancelActivity.emit(activity);
  }

  activityIconWrapperClass(activity: any): string {
    return this.getActivityIconWrapperClassFn
      ? this.getActivityIconWrapperClassFn(activity)
      : '';
  }

  activityIcon(activity: any): string {
    return this.getActivityIconFn ? this.getActivityIconFn(activity) : '';
  }

  activityIconColor(activity: any): string {
    return this.getActivityIconColorFn
      ? this.getActivityIconColorFn(activity)
      : '';
  }

  activityTime(activity: any): string {
    return this.getActivityTimeFn ? this.getActivityTimeFn(activity) : '';
  }

  activityDueDate(activity: any): string {
    return this.getActivityDueDateFn ? this.getActivityDueDateFn(activity) : '';
  }

  userInitials(activity: any): string {
    return this.getUserInitialsFn ? this.getUserInitialsFn(activity) : '';
  }

  userFullName(activity: any): string {
    return this.getUserFullNameFn ? this.getUserFullNameFn(activity) : '';
  }

  activityStatusClass(state: any): string {
    return this.getActivityStatusClassFn
      ? this.getActivityStatusClassFn(state)
      : '';
  }

  activityStatusLabel(state: any): string {
    return this.getActivityStatusLabelFn
      ? this.getActivityStatusLabelFn(state)
      : '';
  }

  get hasActivities(): boolean {
    return (
      Array.isArray(this.activitiesGroupedByDate) &&
      this.activitiesGroupedByDate.length > 0
    );
  }

  get pendingActivitiesGrouped(): {
    date: string;

    label: string;

    activities: any[];
  }[] {
    if (!Array.isArray(this.activitiesGroupedByDate)) return [];

    return this.activitiesGroupedByDate

      .map((g) => ({
        ...g,

        activities: (g.activities || []).filter(
          (a: any) => a.state == 'Planned'
        ),
      }))

      .filter((g) => g.activities.length > 0);
  }

  get hasPendingActivities(): boolean {
    return this.pendingActivitiesGrouped.length > 0;
  }

  get pendingCount(): number {
    return this.pendingActivitiesGrouped.reduce(
      (acc, g) => acc + g.activities.length,

      0
    );
  }

  trackActivity = (_: number, activity: any) => activity?.id ?? _;

  trackMessage = (_: number, msg: any) => msg?.id ?? _;

  get historyActivitiesGrouped(): {
    date: string;

    label: string;

    activities: any[];
  }[] {
    if (!Array.isArray(this.activitiesGroupedByDate)) return [];

    return this.activitiesGroupedByDate

      .map((g) => ({
        ...g,

        activities: (g.activities || []).filter(
          (a: any) => a.state !== 'Planned'
        ),
      }))

      .filter((g) => g.activities.length > 0);
  }

  get hasHistoryActivities(): boolean {
    return this.historyActivitiesGrouped.length > 0;
  }

  get historyCount(): number {
    return this.historyActivitiesGrouped.reduce(
      (acc, g) => acc + g.activities.length,

      0
    );
  }

  historyStatus(activity: any): string {
    if (activity?.hasExpired) return 'Vencida';

    return this.activityStatusLabel(activity?.state);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['activitiesGroupedByDate']) {
      // Si hay actividades pendientes, abrir el acordeón
      if (this.hasPendingActivities) {
        this.pendingCollapsed = false;
      }
      // Si hay historial, abrir el acordeón
      if (this.hasHistoryActivities) {
        this.historyCollapsed = false;
      }
      // Forzar detección de cambios para OnPush
      this.cdr.markForCheck();
    }

    if (changes['opportunity']) {
      // Si hay mensajes, abrir el acordeón (cuando se implemente mensajes fuera del chat)
      if (this.hasSentMessages) {
        this.noteCollapsed = false;
      }
      // Forzar detección de cambios para OnPush
      this.cdr.markForCheck();
    }

    if (changes['loadingActivities']) {
      // Forzar detección de cambios cuando cambia el estado de carga
      this.cdr.markForCheck();
    }
  }

  highlightMentions(text: string): SafeHtml {
    // Regex que detecta '@' seguido de cualquier texto que no contenga '_' y termina con '_'

    const mentionRegex = /@([^_]+)_/g;

    const highlighted = text.replace(mentionRegex, (_, content) => {
      const cleanMention = `@${content.trim()}`;

      return `<span style="color: #1890ff; font-weight: 600;">${cleanMention}</span>`;
    });

    return this.sanitizer.bypassSecurityTrustHtml(highlighted);
  }

  sanitizeHtml(html: string | null | undefined): SafeHtml {
    if (!html) return this.sanitizer.bypassSecurityTrustHtml('');
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private getDateLabel(date: Date | null): string {
    if (!date) return 'Sin fecha';

    const today = new Date();

    const target = new Date(
      date.getFullYear(),

      date.getMonth(),

      date.getDate()
    );

    const current = new Date(
      today.getFullYear(),

      today.getMonth(),

      today.getDate()
    );

    const diffInDays =
      (current.getTime() - target.getTime()) / (1000 * 60 * 60 * 24);

    if (diffInDays === 0) return 'Hoy';

    if (diffInDays === 1) return 'Ayer';

    return date.toLocaleDateString('es-ES', {
      day: '2-digit',

      month: 'short',

      year: 'numeric',
    });
  }

  constructor(
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef
  ) { }
}
